import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Button, Card, Skeleton, TextField } from '../primitives';
import { supabase } from '../../lib/supabase';
import type { Database } from '../../types/database';

type WorkItemRow = Database['public']['Tables']['work_items']['Row'];
type WorkItemType = Database['public']['Enums']['work_item_type'];

export interface SubtasksChecklistProps {
  workItemId: string;
  orgId: string;
  workItemType: WorkItemType;
  onNavigate: (id: string) => void;
  onError: (message: string) => void;
  /** When true, render the rows without the surrounding `<Card>` chrome —
   * the consumer (e.g. InspectorTabs) supplies its own surface + padding. */
  naked?: boolean;
}

interface SubtaskRow {
  id: string;
  name: string;
  status: string;
  updated_at: string;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready' }
  | { kind: 'error'; message: string };

export function SubtasksChecklist({
  workItemId,
  orgId,
  workItemType,
  onNavigate,
  onError,
  naked = false
}: SubtasksChecklistProps) {
  const [load, setLoad] = useState<LoadState>({ kind: 'loading' });
  const [rows, setRows] = useState<SubtaskRow[]>([]);
  const [firstStage, setFirstStage] = useState<string>('To do');
  const [lastStage, setLastStage] = useState<string>('Done');
  const [showAdd, setShowAdd] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [adding, setAdding] = useState(false);

  const canAddSubtask = workItemType !== 'subtask';

  useEffect(() => {
    let cancelled = false;
    setLoad({ kind: 'loading' });
    void (async () => {
      const pipelineRes = await supabase
        .from('work_item_status_pipelines')
        .select('stages')
        .eq('org_id', orgId)
        .eq('applies_to_type', 'subtask')
        .maybeSingle();
      if (cancelled) return;
      const stages = (pipelineRes.data?.stages as string[] | null) ?? null;
      if (Array.isArray(stages) && stages.length >= 1) {
        setFirstStage(stages[0]);
        setLastStage(stages[stages.length - 1]);
      }

      const { data, error } = await supabase
        .from('work_items')
        .select('id, name, status, updated_at')
        .eq('org_id', orgId)
        .eq('parent_work_item_id', workItemId)
        .eq('type', 'subtask')
        .order('created_at', { ascending: true });
      if (cancelled) return;
      if (error) {
        setLoad({ kind: 'error', message: error.message });
        return;
      }
      setRows(
        ((data ?? []) as Pick<WorkItemRow, 'id' | 'name' | 'status' | 'updated_at'>[]).map(
          (r) => ({
            id: r.id as string,
            name: (r.name as string) ?? '',
            status: (r.status as string) ?? '',
            updated_at: r.updated_at as string
          })
        )
      );
      setLoad({ kind: 'ready' });
    })();
    return () => {
      cancelled = true;
    };
  }, [workItemId, orgId]);

  /* Subtasks have exactly one parent — a Story/Bug/Task. There is no
   * "attach existing subtask" flow here (that would mean re-parenting one
   * subtask away from a different Story, which we don't expose). Instead
   * the only Add interaction is "create new" via the inline form below.
   * The Attach picker is reserved for Epic → Child Tasks (ChildTasksPanel),
   * where multiple Story/Bug/Task work items can be linked as children. */
  const onUnlink = useCallback(
    async (subId: string, subName: string) => {
      if (
        !window.confirm(
          'Unlink this subtask? The work item still exists in /work-items with no parent.'
        )
      ) {
        return;
      }
      const { error } = await supabase.rpc('set_work_item_parent', {
        p_work_item_id: subId,
        p_parent_id: null as unknown as string
      });
      if (error) {
        onError(`Could not unlink "${subName || 'subtask'}": ${error.message}`);
        return;
      }
      setRows((prev) => prev.filter((r) => r.id !== subId));
    },
    [onError]
  );

  const onToggle = useCallback(
    async (sub: SubtaskRow, done: boolean) => {
      const nextStatus = done ? lastStage : firstStage;
      const optimistic = rows.map((r) =>
        r.id === sub.id ? { ...r, status: nextStatus } : r
      );
      setRows(optimistic);
      const { error } = await supabase.rpc('set_work_item_status', {
        p_work_item_id: sub.id,
        p_status: nextStatus
      });
      if (error) {
        setRows(rows);
        onError(error.message ?? 'Failed to update subtask.');
        return;
      }
      setRows((prev) =>
        prev.map((r) =>
          r.id === sub.id ? { ...r, status: nextStatus } : r
        )
      );
    },
    [rows, firstStage, lastStage, onError]
  );

  const onAddSubmit = useCallback(async () => {
    const trimmed = newTitle.trim();
    if (!trimmed) return;
    setAdding(true);
    const { data, error } = await supabase.rpc('create_work_item_subtask', {
      p_parent_work_item_id: workItemId,
      p_name: trimmed,
      p_status: firstStage
    });
    setAdding(false);
    const envelope = data as
      | {
          ok?: boolean;
          work_item?: { id: string; name: string; status: string; updated_at: string };
        }
      | null;
    if (error || !envelope?.ok || !envelope.work_item) {
      onError(error?.message ?? 'Failed to add subtask.');
      return;
    }
    const created = envelope.work_item;
    setRows((prev) => [
      ...prev,
      {
        id: created.id,
        name: created.name ?? trimmed,
        status: created.status ?? firstStage,
        updated_at: created.updated_at
      }
    ]);
    setNewTitle('');
    setShowAdd(false);
  }, [newTitle, firstStage, workItemId, onError]);

  const wrap = (
    status: 'loading' | 'ready' | 'error',
    title: string,
    children: ReactNode,
    extraData?: Record<string, string | number>
  ) => {
    if (naked) {
      return (
        <div
          className="wid-subtasks-naked"
          data-testid="work-item-detail-subtasks"
          data-status={status}
          {...(extraData ?? {})}
        >
          {children}
        </div>
      );
    }
    return (
      <Card
        data-testid="work-item-detail-subtasks"
        data-status={status}
        {...(extraData ?? {})}
      >
        <Card.Head title={title} />
        <Card.Body>{children}</Card.Body>
      </Card>
    );
  };

  if (load.kind === 'loading') {
    return wrap('loading', 'Subtasks', <Skeleton width="100%" height={20} />);
  }

  if (load.kind === 'error') {
    return wrap(
      'error',
      'Subtasks',
      <p className="wid-subtasks-error">{load.message}</p>
    );
  }

  const doneCount = rows.filter((r) => r.status === lastStage).length;

  return wrap(
    'ready',
    `Subtasks (${doneCount} of ${rows.length} done)`,
    <>
        {rows.length === 0 ? (
          <p className="wid-subtasks-empty">No subtasks yet.</p>
        ) : (
          <ul className="wid-subtask-list">
            {rows.map((sub) => {
              const done = sub.status === lastStage;
              return (
                <li
                  key={sub.id}
                  className="wid-subtask-row"
                  data-testid={`work-item-detail-subtasks-row-${sub.id}`}
                  data-done={done ? 'true' : 'false'}
                >
                  <input
                    type="checkbox"
                    className="wid-subtask-row-checkbox"
                    checked={done}
                    onChange={(e) => void onToggle(sub, e.target.checked)}
                    aria-label={`Toggle subtask ${sub.name} done`}
                    data-testid={`work-item-detail-subtasks-toggle-${sub.id}`}
                  />
                  <button
                    type="button"
                    className="wid-subtask-row-title"
                    onClick={() => onNavigate(sub.id)}
                    data-testid={`work-item-detail-subtasks-title-${sub.id}`}
                    data-done={done ? 'true' : 'false'}
                  >
                    {sub.name}
                  </button>
                  {canAddSubtask ? (
                    <button
                      type="button"
                      className="wid-row-unlink"
                      onClick={() => void onUnlink(sub.id, sub.name)}
                      aria-label={`Unlink ${sub.name || 'subtask'}`}
                      title="Unlink (does not delete the work item)"
                      data-testid={`work-item-detail-subtasks-unlink-${sub.id}`}
                    >
                      ×
                    </button>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}

        {canAddSubtask ? (
          showAdd ? (
            <form
              className="wid-subtasks-add-form"
              data-testid="work-item-detail-subtasks-add-form"
              onSubmit={(e) => {
                e.preventDefault();
                void onAddSubmit();
              }}
            >
              <TextField
                id="work-item-detail-subtasks-add-input"
                label="New subtask title"
                hideLabel
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                data-testid="work-item-detail-subtasks-add-input"
                placeholder="Subtask title"
                autoFocus
              />
              <div className="wid-subtasks-add-actions">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowAdd(false);
                    setNewTitle('');
                  }}
                  data-testid="work-item-detail-subtasks-add-cancel"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  size="sm"
                  disabled={adding || newTitle.trim().length === 0}
                  data-testid="work-item-detail-subtasks-add-save"
                >
                  {adding ? 'Saving…' : 'Save'}
                </Button>
              </div>
            </form>
          ) : (
            <button
              type="button"
              className="wid-add-row"
              onClick={() => setShowAdd(true)}
              data-testid="work-item-detail-subtasks-add"
            >
              + Add subtask
            </button>
          )
        ) : null}
    </>,
    { 'data-count': rows.length }
  );
}
